import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { ChatWindow } from "@/components/chat/ChatWindow";
import {
  Copy,
  Users,
  Loader2,
  Save,
  Database,
  User,
  Building,
  ListTodo,
} from "lucide-react";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { useNavigate } from "react-router-dom";
import { configService } from "@/services/config";
import { Switch } from "@/components/ui/switch";
import { useOnboardingConfig } from "@/components/onboarding/OnboardingSteps";

const defaultSettings = {
  companyName: "Support",
  agentName: "AI Assistant",
  minimizedText: "Chat with us",
  initialMessage: "Hi! I'm here to help. What brings you here today?",
  accentColor: "#0ea5e9",
  position: "bottom-right" as const,
  showIconOnly: true,
  useProfileContent: true,
  useListings: true,
  stream: true,
  stream_speed: 100,
  dataSources: {
    knowledgeGraph: true,
    profile: true,
    communityListings: true,
    corporateListings: true,
  },
};

interface ChatSettings {
  companyName: string;
  agentName: string;
  minimizedText: string;
  initialMessage: string;
  accentColor: string;
  position: "bottom-right" | "bottom-left";
  showIconOnly: boolean;
  useProfileContent: boolean;
  useListings: boolean;
  stream: boolean;
  stream_speed: number;
  dataSources: {
    knowledgeGraph: boolean;
    profile: boolean;
    communityListings: boolean;
    corporateListings: boolean;
  };
}

export function ChatPage({
  profile,
  session,
}: {
  profile: any | null;
  session: any | null;
}) {
  const { toast } = useToast();
  const supabase = useSupabaseClient();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<ChatSettings>(defaultSettings);
  const [localSettings, setLocalSettings] =
    useState<ChatSettings>(defaultSettings);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { updateConfig } = useOnboardingConfig();

  // Load settings from config API
  useEffect(() => {
    if (session?.access_token) {
      configService.updateToken(session.access_token);
      fetchSettings();
    }
  }, [session?.access_token]);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await configService.getConfig("npo.impactassistant.bot");
      if (response.success && response.data) {
        // Ensure dataSources is properly initialized by merging with defaults
        const loadedSettings = {
          ...defaultSettings,
          ...response.data,
          dataSources: {
            ...defaultSettings.dataSources,
            ...(response.data.dataSources || {}),
          },
        };
        setSettings(loadedSettings);
        setLocalSettings(loadedSettings);
      }
    } catch (error) {
      console.error("Error loading chat settings:", error);
      toast({
        title: "Error",
        description: "Failed to load chat settings",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Update settings in config API
  const saveSettings = async () => {
    setIsSaving(true);
    try {
      const response = await configService.setConfig(
        "npo.impactassistant.bot",
        {
          value: localSettings,
          description: "Impact Assistant Bot Configuration",
          isPublic: true,
        }
      );
      if (response) {
        setSettings(localSettings);
        setHasChanges(false);
        updateConfig({ ai_chat: true });
        toast({
          title: "Success",
          description: "Chat settings saved successfully",
        });
      } else {
        throw new Error("Failed to update settings");
      }
    } catch (error) {
      console.error("Error saving chat settings:", error);
      toast({
        title: "Error",
        description: "Failed to save chat settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Update local settings without saving to API
  const updateLocalSettings = (updates: Partial<ChatSettings>) => {
    const newSettings = { ...localSettings, ...updates };
    setLocalSettings(newSettings);
    setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(settings));
  };

  useEffect(() => {
    if (profile?.id) {
      setLocalSettings((prev) => ({
        ...prev,
      }));
    }
  }, [profile?.id]);

  useEffect(() => {
    if (!supabase || !profile?.id) return;

    // Load user's NPO profile
    const loadProfile = async () => {
      const { data: profile, error } = await supabase
        .from("npo_profiles")
        .select("organization_name")
        .eq("id", profile.id)
        .single();

      if (error) {
        console.error("Error loading profile:", error);
        return;
      }

      if (profile) {
        updateLocalSettings({
          companyName: profile.organization_name,
        });
      }
    };

    loadProfile();
  }, [profile?.id, supabase]);

  // Generate embed code for the chat widget
  const getEmbedCode = () => {
    return `<!-- Add this to your HTML where you want the chat widget to appear -->
<script src="https://causeconnect.ai/services/chat-widget.js" data-publisher-id="${profile?.id}"></script>`;
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({
      title: "Code copied!",
      description: "The code has been copied to your clipboard.",
    });
  };

  // Function to toggle a data source and save automatically
  const toggleAndSaveDataSource = async (
    key: keyof typeof localSettings.dataSources,
    checked: boolean
  ) => {
    // Update local state first
    const updatedSettings = {
      ...localSettings,
      dataSources: {
        ...localSettings.dataSources,
        [key]: checked,
      },
    };

    setLocalSettings(updatedSettings);
    setHasChanges(true);

    // Save immediately
    try {
      setIsSaving(true);
      const response = await configService.setConfig(
        "npo.impactassistant.bot",
        {
          value: updatedSettings,
          isPublic: true,
          description: "Impact Assistant Bot Configuration",
        }
      );

      if (response) {
        toast({
          title: "Success",
          description: `${key} data source ${
            checked ? "enabled" : "disabled"
          } successfully`,
        });
        setHasChanges(false);
      } else {
        throw new Error(`Failed to update ${key} data source`);
      }
    } catch (error) {
      console.error(`Error updating ${key} data source:`, error);
      toast({
        title: "Error",
        description: `Failed to update ${key} data source`,
        variant: "destructive",
      });

      // Revert local state on error
      setLocalSettings((prev) => ({
        ...prev,
        dataSources: {
          ...prev.dataSources,
          [key]: !checked,
        },
      }));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Website Chat Widget</h1>
        <Button
          variant="outline"
          onClick={() => navigate("/dashboard/chatbot/leads")}
        >
          <Users className="h-4 w-4 mr-2" />
          My Leads
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Chat Widget Settings</CardTitle>
            <CardDescription>
              Customize how your chat widget looks and behaves
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  value={localSettings.companyName}
                  onChange={(e) =>
                    updateLocalSettings({ companyName: e.target.value })
                  }
                  placeholder="Support"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="agentName">Agent Name</Label>
                <Input
                  id="agentName"
                  value={localSettings.agentName}
                  onChange={(e) =>
                    updateLocalSettings({ agentName: e.target.value })
                  }
                  placeholder="AI Assistant"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="initialMessage">Initial Message</Label>
                <Input
                  id="initialMessage"
                  value={localSettings.initialMessage}
                  onChange={(e) =>
                    updateLocalSettings({ initialMessage: e.target.value })
                  }
                  placeholder="Hi! I'm here to help. What brings you here today?"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="accentColor">Accent Color</Label>
                <div className="flex space-x-2">
                  <Input
                    id="accentColor"
                    type="color"
                    value={localSettings.accentColor}
                    onChange={(e) =>
                      updateLocalSettings({ accentColor: e.target.value })
                    }
                    className="w-12 p-1 h-9"
                  />
                  <Input
                    value={localSettings.accentColor}
                    onChange={(e) =>
                      updateLocalSettings({ accentColor: e.target.value })
                    }
                    placeholder="#0ea5e9"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Position</Label>
                <div className="grid grid-cols-4 gap-2">
                  <Button
                    variant="outline"
                    className={
                      localSettings.position === "bottom-left"
                        ? "border-2 border-primary bg-primary/10"
                        : ""
                    }
                    onClick={() =>
                      updateLocalSettings({ position: "bottom-left" })
                    }
                  >
                    Bottom Left
                  </Button>
                  <Button
                    variant="outline"
                    className={
                      localSettings.position === "bottom-right"
                        ? "border-2 border-primary bg-primary/10"
                        : ""
                    }
                    onClick={() =>
                      updateLocalSettings({ position: "bottom-right" })
                    }
                  >
                    Bottom Right
                  </Button>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="showIconOnly"
                      checked={localSettings.showIconOnly}
                      onChange={(e) =>
                        updateLocalSettings({
                          showIconOnly: e.target.checked,
                        })
                      }
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <Label
                      htmlFor="showIconOnly"
                      className="text-sm font-normal"
                    >
                      Show Only Icon
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="stream"
                      checked={localSettings.stream}
                      onChange={(e) =>
                        updateLocalSettings({ stream: e.target.checked })
                      }
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <Label
                      htmlFor="stream"
                      className="text-sm font-normal"
                    >
                      Streaming
                    </Label>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-6"></div>
              </div>

              {!localSettings.showIconOnly && (
                <div className="space-y-2">
                  <Label htmlFor="minimizedText">Minimized Text</Label>
                  <Input
                    id="minimizedText"
                    value={localSettings.minimizedText}
                    onChange={(e) =>
                      updateLocalSettings({ minimizedText: e.target.value })
                    }
                    placeholder="Chat with us"
                  />
                </div>
              )}

              <div className="border-t pt-4 mt-4 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setLocalSettings(defaultSettings);
                    setHasChanges(true);
                  }}
                >
                  Reset to Default
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? "Hide Preview" : "Preview Widget"}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={saveSettings}
                  disabled={!hasChanges || isSaving}
                  className="gap-2"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Data Sources
              </CardTitle>
              <CardDescription>
                Configure which data sources the chat widget can access to
                provide better responses.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start justify-between space-x-4 rounded-lg border p-4">
                  <div className="flex items-start space-x-4">
                    <div className="mt-0.5 bg-gray-100 p-2 rounded-md">
                      <Database className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <Label
                        htmlFor="knowledgeGraph"
                        className="text-base font-semibold"
                      >
                        Knowledge Graph
                      </Label>
                      <p className="text-sm text-gray-500">
                        Allow the chat to access your organization's knowledge
                        graph data.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="knowledgeGraph"
                    checked={localSettings.dataSources.knowledgeGraph}
                    onCheckedChange={(checked) =>
                      toggleAndSaveDataSource("knowledgeGraph", checked)
                    }
                  />
                </div>

                <div className="flex items-start justify-between space-x-4 rounded-lg border p-4">
                  <div className="flex items-start space-x-4">
                    <div className="mt-0.5 bg-gray-100 p-2 rounded-md">
                      <User className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <Label
                        htmlFor="profile"
                        className="text-base font-semibold"
                      >
                        Your Profile
                      </Label>
                      <p className="text-sm text-gray-500">
                        Allow the chat to access information from your
                        organization profile.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="profile"
                    checked={localSettings.dataSources.profile}
                    onCheckedChange={(checked) =>
                      toggleAndSaveDataSource("profile", checked)
                    }
                  />
                </div>

                <div className="flex items-start justify-between space-x-4 rounded-lg border p-4">
                  <div className="flex items-start space-x-4">
                    <div className="mt-0.5 bg-gray-100 p-2 rounded-md">
                      <ListTodo className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <Label
                        htmlFor="communityListings"
                        className="text-base font-semibold"
                      >
                        Community Listings
                      </Label>
                      <p className="text-sm text-gray-500">
                        Allow the chat to access and recommend community
                        volunteer opportunities.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="communityListings"
                    checked={localSettings.dataSources.communityListings}
                    onCheckedChange={(checked) =>
                      toggleAndSaveDataSource("communityListings", checked)
                    }
                  />
                </div>

                <div className="flex items-start justify-between space-x-4 rounded-lg border p-4">
                  <div className="flex items-start space-x-4">
                    <div className="mt-0.5 bg-gray-100 p-2 rounded-md">
                      <Building className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <Label
                        htmlFor="corporateListings"
                        className="text-base font-semibold"
                      >
                        Corporate Listings
                      </Label>
                      <p className="text-sm text-gray-500">
                        Allow the chat to access and recommend corporate
                        partnership opportunities.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="corporateListings"
                    checked={localSettings.dataSources.corporateListings}
                    onCheckedChange={(checked) =>
                      toggleAndSaveDataSource("corporateListings", checked)
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Installation Instructions</CardTitle>
              <CardDescription>
                Follow these steps to add the chat widget to your website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="space-y-2">
                  <h3 className="font-medium">Publisher ID</h3>
                  <p className="text-sm text-muted-foreground">
                    Your publisher ID is unique to your organization.
                  </p>
                  <div className="relative">
                    <Input
                      readOnly
                      value={profile?.id}
                      className="w-full bg-muted p-4 rounded-lg text-sm"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute top-2 right-2 h-8 w-8"
                      onClick={() => handleCopyCode(profile?.id)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <h3 className="font-medium pt-4">1. Copy the Code</h3>
                <p className="text-sm text-muted-foreground">
                  Copy and paste this code into your website:
                </p>
                <div className="relative">
                  <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap break-all">
                    <code>{getEmbedCode()}</code>
                  </pre>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="absolute top-2 right-2 h-8 w-8"
                    onClick={() => handleCopyCode(getEmbedCode())}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  <strong>Note:</strong> The chat widget will automatically load
                  your saved settings from our servers. Any changes you make
                  here will be reflected in your widget after you click "Save
                  Changes".
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium">2. Add to Your Website</h3>
                <p className="text-sm text-muted-foreground">
                  Add the code to your website's HTML file, preferably just
                  before the closing &lt;/body&gt; tag.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview Chat Window */}
        {showPreview && (
          <ChatWindow
            companyName={localSettings.companyName}
            agentName={localSettings.agentName}
            minimizedText={localSettings.minimizedText}
            initialMessage={localSettings.initialMessage}
            accentColor={localSettings.accentColor}
            position={localSettings.position as "bottom-right" | "bottom-left"}
            publisherId={profile?.id || ""}
            showIconOnly={localSettings.showIconOnly}
            useProfileContent={localSettings.useProfileContent}
            useListings={localSettings.useListings}
          />
        )}
      </div>
    </div>
  );
}

export default ChatPage;
